import * as XLSX from 'xlsx';

const RENDERING_TYPES = [
  { value: 'Html', name: 'Standard (index.html)' },
  { value: 'JavaScript', name: 'Old (phoenixcfg.json)' },
  { value: 'External', name: 'External (URL to other website)' },
];

const ASPECT_RATIOS = [
  { value: null, name: '' },
  { value: 4 / 3, name: '4:3' },
  { value: 16 / 9, name: '16:9' },
];

const STATUS_ITEMS = [
  { text: '', value: 'not_set' },
  { text: 'Enabled', value: 'enabled' },
  { text: 'Disabled', value: 'disabled' },
];

// Explicit Excel column widths (wch), keyed by field label.
const COLUMN_WIDTHS = {
  // Identity
  'ID': 8,
  'Feed Game ID': 12,
  'Game ID': 10,
  'Slug': 22,
  'Name': 28,
  'Alias': 22,
  'Tier': 8,
  'Developer': 22,
  'Feed': 20,
  'Feed Environment': 16,
  'Version': 14,
  'Meta Template ID': 14,
  'Meta Template Name': 28,

  // Tech stack
  'Rendering Type': 28,
  'SDK': 14,
  'Engine': 14,
  'Engine Version': 14,
  'Storage Path (deprecated)': 28,

  // Booleans / short flags
  'Auth': 10,
  'Eagle': 10,
  'Inventory': 12,
  'Collection': 12,
  'Leaderboard': 14,
  'Multiplayer': 12,
  'Platform Game End': 16,
  'Cloud Save': 12,
  'Gems': 10,
  'Interstitial Ads': 14,
  'Rewarded Ads': 14,
  'Ads Free': 10,
  'Ad Podding': 12,
  'Rendering in GRM': 14,
  'Rendering in IFrame': 16,
  'Flexible Size': 12,
  'Full Screen': 12,
  'Portrait Mode': 14,
  'Coming Soon': 12,
  'Subscriber Only': 14,

  // Text / medium fields
  'Cloud Save Storage': 22,
  'MTX Details': 28,
  'AD Layout Code': 16,
  'Aspect Ratio': 12,
  'Iframe Allow Options': 36,
  'Coming Soon Date': 14,
  'Subscriber Only Date': 18,

  // Preroll
  'Preroll status': 14,
  'Free Period (days)': 16,
  'Duration (sec)': 14,
  'First Start Rate': 14,
  'Show Rate': 12,

  // Support
  'Status': 12,
  'Description': 36,
  'Email Text': 18,
  'Email': 28,
  'Phone Text': 18,
  'Phone': 16,

  // Live ops / URLs
  'Live Ops Dependencies': 28,
  'Webhook URL (Dev)': 36,
  'Webhook URL (Live)': 36,
};

function columnWidthForLabel(label) {
  if (Object.prototype.hasOwnProperty.call(COLUMN_WIDTHS, label)) {
    return { wch: COLUMN_WIDTHS[label] };
  }
  // Fallback: fit the header with a sensible min/max
  return { wch: Math.min(36, Math.max(12, String(label || '').length + 2)) };
}

export { columnWidthForLabel };

function yesNo(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
}

function lookupLabel(items, value, textKey = 'name', valueKey = 'value') {
  const found = (items || []).find((i) => i[valueKey] === value);
  if (!found) return value ?? '';
  const label = found[textKey];
  if (label == null) return '';
  // Keep Excel cells empty instead of UI "Not set" placeholders
  if (String(label).trim().toLowerCase() === 'not set') return '';
  return label;
}

function ternaryStatusLabel(value) {
  if (value === null || value === undefined) return '';
  return value ? 'Enabled' : 'Disabled';
}

function overrideLabel(value) {
  if (value === true) return 'True';
  if (value === false) return 'False';
  return 'Inherit';
}

function inheritStatusLabel(value) {
  if (value === null || value === undefined) return 'Inherit';
  return value ? 'Enabled' : 'Disabled';
}

function resolveDeveloperName(item, developers = []) {
  if (item?.developer?.name) return item.developer.name;
  const id = item?.developerId ?? item?.developer?.id;
  if (id == null) return '';
  const found = developers.find((d) => d.id === id || String(d.id) === String(id));
  return found?.name || '';
}

function resolveMetaTemplate(item, metaTemplates = []) {
  const id = item?.metaTemplateId ?? item?.metaTemplate?.id;
  if (id == null) {
    return { id: '', name: item?.metaTemplate?.name || '' };
  }
  const found = metaTemplates.find((t) => t.id === id || String(t.id) === String(id));
  return {
    id,
    name: found?.name || item?.metaTemplate?.name || '',
  };
}

/**
 * Build form-mirrored export sections for a single game.
 * @param {object} item
 * @param {{ developers?: array, metaTemplates?: array }} lookups
 */
export function buildGameExportSections(item = {}, lookups = {}) {
  const { developers = [], metaTemplates = [] } = lookups;
  const preroll = item.gamePrerollSettings || {};
  const support = item.supportInfo || {};
  const meta = resolveMetaTemplate(item, metaTemplates);
  const supportEnabled = support.isEnabled ?? support.active;

  return [
    {
      title: 'Identity',
      fields: [
        { label: 'ID', value: item.id },
        { label: 'Slug', value: item.slug },
        { label: 'Name', value: item.name },
        { label: 'Tier', value: item.tier },
        { label: 'Developer', value: resolveDeveloperName(item, developers) },
        { label: 'Meta Template ID', value: meta.id },
        { label: 'Meta Template Name', value: meta.name },
      ],
    },
    {
      title: 'Tech stack',
      fields: [
        {
          label: 'Rendering Type',
          value: lookupLabel(RENDERING_TYPES, item.renderingType),
        },
        { label: 'SDK', value: item.sdk },
        { label: 'Engine', value: item.engine },
        { label: 'Engine Version', value: item.engineVersion },
        { label: 'Storage Path (deprecated)', value: item.sdkName },
      ],
    },
    {
      title: 'Platform / auth / session',
      fields: [
        { label: 'Auth', value: yesNo(item.hasAuth) },
        { label: 'Eagle', value: yesNo(item.hasEagle) },
      ],
    },
    {
      title: 'Progress & persistence',
      fields: [
        { label: 'Inventory', value: yesNo(item.hasInventory) },
        { label: 'Collection', value: yesNo(item.hasCollection) },
        { label: 'Leaderboard', value: yesNo(item.hasLeaderboard) },
        { label: 'Multiplayer', value: yesNo(item.hasMultiplayer) },
        { label: 'Platform Game End', value: yesNo(item.hasPlatformGameEnd) },
        { label: 'Cloud Save', value: yesNo(item.hasCloudSave) },
        { label: 'Cloud Save Storage', value: item.cloudSaveStorage },
      ],
    },
    {
      title: 'Monetization (MTX / ads)',
      fields: [
        { label: 'Gems', value: yesNo(item.hasGems) },
        { label: 'Interstitial Ads', value: yesNo(item.hasInterstitialAds) },
        { label: 'Rewarded Ads', value: yesNo(item.hasRewardedAds) },
        { label: 'Ads Free', value: yesNo(item.isAdsFree) },
        { label: 'Ad Podding', value: yesNo(item.adPodding) },
        { label: 'MTX Details', value: item.mtxDetails },
        { label: 'AD Layout Code', value: item.adLayoutCode },
      ],
    },
    {
      title: 'Game Preroll Settings',
      fields: [
        { label: 'Preroll status', value: ternaryStatusLabel(preroll.isEnabled) },
        { label: 'Free Period (days)', value: preroll.freePeriod },
        { label: 'Duration (sec)', value: preroll.seconds },
        { label: 'First Start Rate', value: preroll.firstStartRate },
        { label: 'Show Rate', value: preroll.showRate },
      ],
    },
    {
      title: 'Rendering & layout',
      fields: [
        { label: 'Rendering in GRM', value: yesNo(item.isGRM) },
        { label: 'Rendering in IFrame', value: yesNo(item.isIframe) },
        { label: 'Flexible Size', value: yesNo(item.isFlex) },
        { label: 'Full Screen', value: yesNo(item.hasFullscreen) },
        { label: 'Portrait Mode', value: yesNo(item.hasPortraitMode) },
        {
          label: 'Aspect Ratio',
          value: lookupLabel(ASPECT_RATIOS, item.aspectRatio),
        },
        { label: 'Iframe Allow Options', value: item.iframeAllowOptions },
      ],
    },
    {
      title: 'Availability',
      fields: [
        { label: 'Coming Soon', value: yesNo(item.hasComingSoon) },
        { label: 'Coming Soon Date', value: item.comingSoonDate },
        { label: 'Subscriber Only', value: yesNo(item.isSubscriberOnly) },
        { label: 'Subscriber Only Date', value: item.isSubscriberOnlyDate },
      ],
    },
    {
      title: 'Support Info',
      fields: [
        { label: 'Status', value: ternaryStatusLabel(supportEnabled) },
        { label: 'Description', value: support.description },
        { label: 'Email Text', value: support.emailText },
        { label: 'Email', value: support.email },
        { label: 'Phone Text', value: support.phoneText },
        { label: 'Phone', value: support.phone },
      ],
    },
    {
      title: 'Live ops',
      fields: [
        { label: 'Live Ops Dependencies', value: item.liveOpsDependencies },
        { label: 'Webhook URL (Dev)', value: item.webhookDev },
        { label: 'Webhook URL (Live)', value: item.webhookLive },
      ],
    },
  ];
}

function buildGroupedHeaderRows(sections) {
  const groupRow = [];
  const labelRow = [];
  const merges = [];
  let col = 0;

  sections.forEach((section) => {
    const start = col;
    section.fields.forEach((field, index) => {
      groupRow.push(index === 0 ? section.title : '');
      labelRow.push(field.label);
      col += 1;
    });
    if (section.fields.length > 1) {
      merges.push({ s: { r: 0, c: start }, e: { r: 0, c: col - 1 } });
    }
  });

  return { groupRow, labelRow, merges };
}

function writeGroupedExport(items, buildSections, options = {}) {
  const { filename, sheetName = 'Sheet1' } = options;
  if (!filename) {
    throw new Error('filename is required');
  }

  const list = Array.isArray(items) ? items : [];
  const templateSections = buildSections(list[0] || {});
  const { groupRow, labelRow, merges } = buildGroupedHeaderRows(templateSections);

  const valueRows = list.map((item) => {
    const sections = buildSections(item);
    const row = [];
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        row.push(field.value == null ? '' : field.value);
      });
    });
    return row;
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([groupRow, labelRow, ...valueRows]);
  ws['!merges'] = merges;
  ws['!cols'] = labelRow.map((label) => columnWidthForLabel(label));
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);

  return { filename, count: list.length };
}

/**
 * Build form-mirrored export sections for a feed game (with Inherit overrides).
 * @param {object} feedGame
 * @param {{ feed?: object, metaTemplates?: array }} lookups
 */
export function buildFeedGameExportSections(feedGame = {}, lookups = {}) {
  const { feed = {}, metaTemplates = [] } = lookups;
  const game = feedGame.game || {};
  const preroll = feedGame.feedGamePrerollSettings || feedGame.gamePrerollSettings || {};
  const support = feedGame.supportInfo || {};
  const meta = resolveMetaTemplate(
    {
      metaTemplateId: feedGame.metaTemplateId ?? game.metaTemplateId,
      metaTemplate: feedGame.metaTemplate || game.metaTemplate,
    },
    metaTemplates
  );
  const supportEnabled = support.isEnabled ?? support.active;
  const version =
    feedGame.gameVersion?.version
    || feedGame.gameVersion?.name
    || feedGame.version
    || feedGame.gameVersionId
    || '';

  return [
    {
      title: 'Identity',
      fields: [
        { label: 'Feed Game ID', value: feedGame.id },
        { label: 'Game ID', value: feedGame.gameId ?? game.id },
        { label: 'Slug', value: game.slug },
        { label: 'Name', value: game.name },
        { label: 'Alias', value: feedGame.alias },
        { label: 'Feed', value: feed.name || '' },
        { label: 'Feed Environment', value: feed.environment || '' },
        { label: 'Version', value: version },
        { label: 'Meta Template ID', value: meta.id },
        { label: 'Meta Template Name', value: meta.name },
      ],
    },
    {
      title: 'Platform / auth / session',
      fields: [
        { label: 'Auth', value: overrideLabel(feedGame.hasAuth) },
        { label: 'Eagle', value: overrideLabel(feedGame.hasEagle) },
      ],
    },
    {
      title: 'Progress & persistence',
      fields: [
        { label: 'Inventory', value: overrideLabel(feedGame.hasInventory) },
        { label: 'Collection', value: overrideLabel(feedGame.hasCollection) },
        { label: 'Leaderboard', value: overrideLabel(feedGame.hasLeaderboard) },
        { label: 'Multiplayer', value: overrideLabel(feedGame.hasMultiplayer) },
        { label: 'Platform Game End', value: overrideLabel(feedGame.hasPlatformGameEnd) },
        { label: 'Cloud Save', value: overrideLabel(feedGame.hasCloudSave) },
        { label: 'Cloud Save Storage', value: feedGame.cloudSaveStorage },
      ],
    },
    {
      title: 'Monetization (MTX / ads)',
      fields: [
        { label: 'Gems', value: overrideLabel(feedGame.hasGems) },
        { label: 'Interstitial Ads', value: overrideLabel(feedGame.hasInterstitialAds) },
        { label: 'Rewarded Ads', value: overrideLabel(feedGame.hasRewardedAds) },
        { label: 'Ads Free', value: overrideLabel(feedGame.isAdsFree) },
        { label: 'AD Layout Code', value: feedGame.adLayoutCode },
      ],
    },
    {
      title: 'Feed Game Preroll Settings',
      fields: [
        { label: 'Preroll status', value: inheritStatusLabel(preroll.isEnabled) },
        { label: 'Free Period (days)', value: preroll.freePeriod },
        { label: 'Duration (sec)', value: preroll.seconds },
        { label: 'First Start Rate', value: preroll.firstStartRate },
        { label: 'Show Rate', value: preroll.showRate },
      ],
    },
    {
      title: 'Rendering & layout',
      fields: [
        { label: 'Rendering in GRM', value: overrideLabel(feedGame.isGRM) },
        { label: 'Rendering in IFrame', value: overrideLabel(feedGame.isIframe) },
        { label: 'Flexible Size', value: overrideLabel(feedGame.isFlex) },
        { label: 'Full Screen', value: overrideLabel(feedGame.hasFullscreen) },
        { label: 'Portrait Mode', value: overrideLabel(feedGame.hasPortraitMode) },
        { label: 'Aspect Ratio', value: feedGame.aspectRatio },
      ],
    },
    {
      title: 'Availability',
      fields: [
        { label: 'Coming Soon', value: overrideLabel(feedGame.hasComingSoon) },
        { label: 'Coming Soon Date', value: feedGame.comingSoonDate },
        { label: 'Subscriber Only', value: overrideLabel(feedGame.isSubscriberOnly) },
        { label: 'Subscriber Only Date', value: feedGame.isSubscriberOnlyDate },
      ],
    },
    {
      title: 'Support Info',
      fields: [
        { label: 'Status', value: inheritStatusLabel(supportEnabled) },
        { label: 'Description', value: support.description },
        { label: 'Email Text', value: support.emailText },
        { label: 'Email', value: support.email },
        { label: 'Phone Text', value: support.phoneText },
        { label: 'Phone', value: support.phone },
      ],
    },
  ];
}

/**
 * Export one or many games with form-section grouping headers.
 * @param {object[]} items
 * @param {{ developers?: array, metaTemplates?: array, filename: string, sheetName?: string }} options
 */
export function exportGamesToExcel(items, options = {}) {
  const {
    developers = [],
    metaTemplates = [],
    filename,
    sheetName = 'Games',
  } = options;

  const lookups = { developers, metaTemplates };
  return writeGroupedExport(
    items,
    (item) => buildGameExportSections(item, lookups),
    { filename, sheetName }
  );
}

/**
 * Export feed games with form-section grouping headers (Inherit-aware).
 * @param {object[]} items
 * @param {{ feed?: object, metaTemplates?: array, filename: string, sheetName?: string }} options
 */
export function exportFeedGamesToExcel(items, options = {}) {
  const {
    feed = {},
    metaTemplates = [],
    filename,
    sheetName = 'Feed Games',
  } = options;

  const lookups = { feed, metaTemplates };
  return writeGroupedExport(
    items,
    (item) => buildFeedGameExportSections(item, lookups),
    { filename, sheetName }
  );
}

export { STATUS_ITEMS, RENDERING_TYPES, ASPECT_RATIOS };
